import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  Users, Search, UserCheck, UserX, Truck, Store, User,
  Mail, Phone, Calendar, Filter, MoreVertical, Eye, Trash2,
  CheckCircle, XCircle, Clock, Ban, Unlock, AlertTriangle,
  Power, PowerOff, Scissors, MapPin
} from "lucide-react";
import DuplicateUsersPanel from "./DuplicateUsersPanel";
import { UserProfileSheet } from "@/components/admin/UserProfileSheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUtilisateurs({ onRefresh }) {
  const [users, setUsers] = useState([]);
  const [profileId, setProfileId] = useState(null);
  const closeProfile = useCallback(() => setProfileId(null), []);
  const [pendingCouriers, setPendingCouriers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    clients: 0,
    partners: 0,
    couriers: 0,
    vendors: 0,
    pendingCouriers: 0,
    blocked: 0,
    active: 0
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const [usersRes, pendingRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/couriers/pending`)
      ]);

      const usersData = usersRes.data;
      setUsers(usersData);
      setPendingCouriers(pendingRes.data);

      // Calculate stats
      setStats({
        total: usersData.length,
        clients: usersData.filter(u => u.role === 'client').length,
        partners: usersData.filter(u => u.role === 'partner').length,
        couriers: usersData.filter(u => u.role === 'courier').length,
        vendors: usersData.filter(u => u.role === 'vendor').length,
        pendingCouriers: pendingRes.data.length,
        blocked: usersData.filter(u => u.is_blocked).length,
        active: usersData.filter(u => !u.is_blocked && u.is_approved !== false).length
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Erreur lors du chargement des utilisateurs");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const approveCourier = async (courierId) => {
    try {
      await axios.put(`${API}/couriers/${courierId}/approve`);
      toast.success("Livreur/Chauffeur approuvé!");
      fetchUsers();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error("Erreur lors de l'approbation");
    }
  };

  const rejectCourier = async (courierId) => {
    if (!window.confirm("Rejeter cette candidature ?")) return;
    try {
      await axios.put(`${API}/couriers/${courierId}/reject`);
      toast.success("Candidature rejetée");
      fetchUsers();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const activateUser = async (userId, userName) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/unblock`);
      toast.success(`Compte de ${userName} activé!`);
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de l'activation");
    }
  };

  const deactivateUser = async (userId, userName) => {
    if (!window.confirm(`Désactiver le compte de ${userName} ? L'utilisateur ne pourra plus se connecter.`)) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/block`);
      toast.success(`Compte de ${userName} désactivé`);
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la désactivation");
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Supprimer cet utilisateur ? Cette action est irréversible.")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("Utilisateur supprimé");
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const blockUser = async (userId) => {
    if (!window.confirm("Bloquer cet utilisateur ? Il ne pourra plus se connecter.")) return;
    try {
      await axios.put(`${API}/admin/users/${userId}/block`);
      toast.success("Utilisateur bloqué");
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors du blocage");
    }
  };

  const unblockUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/unblock`);
      toast.success("Utilisateur débloqué");
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors du déblocage");
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'client': return <User className="w-4 h-4" />;
      case 'partner': return <Store className="w-4 h-4" />;
      case 'courier': return <Truck className="w-4 h-4" />;
      case 'vendor': return <Scissors className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      client: "bg-blue-100 text-blue-700",
      partner: "bg-green-100 text-green-700",
      courier: "bg-orange-100 text-orange-700",
      vendor: "bg-purple-100 text-purple-700"
    };
    const labels = {
      client: "Client",
      partner: "Partenaire",
      courier: "Livreur/Chauffeur",
      vendor: "Vendeur",
      artisan: "Artisan"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${styles[role] || styles.client}`}>
        {getRoleIcon(role)}
        {labels[role] || role}
      </span>
    );
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <Card className="bg-gradient-to-br from-slate-700 to-slate-800 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">Actifs</p>
                <p className="text-2xl font-bold">{stats.active}</p>
              </div>
              <Power className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">Désactivés</p>
                <p className="text-2xl font-bold">{stats.blocked}</p>
              </div>
              <PowerOff className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">Clients</p>
                <p className="text-2xl font-bold">{stats.clients}</p>
              </div>
              <User className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">Partenaires</p>
                <p className="text-2xl font-bold">{stats.partners}</p>
              </div>
              <Store className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">Livreurs</p>
                <p className="text-2xl font-bold">{stats.couriers}</p>
              </div>
              <Truck className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">ARTISANS</p>
                <p className="text-2xl font-bold">{stats.vendors}</p>
              </div>
              <Scissors className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-0">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs opacity-80">En attente</p>
                <p className="text-2xl font-bold">{stats.pendingCouriers}</p>
              </div>
              <Clock className="w-8 h-8 opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Nettoyage des comptes doublons */}
      <DuplicateUsersPanel onRefresh={fetchUsers} />

      {/* Pending Couriers */}
      {pendingCouriers.length > 0 && (
        <Card className="border-l-4 border-l-yellow-500 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-700">
              <Clock className="w-5 h-5" />
              Livreurs en attente d'approbation ({pendingCouriers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingCouriers.map((courier) => (
                <div key={courier.id} className="border rounded-xl p-4 bg-yellow-50">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-bold">{courier.full_name}</h4>
                      <p className="text-sm text-muted-foreground">{courier.email}</p>
                    </div>
                    <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full text-xs">
                      En attente
                    </span>
                  </div>
                  <div className="space-y-1 text-sm mb-4">
                    <p><Phone className="w-3 h-3 inline mr-1" /> {courier.phone || "Non renseigné"}</p>
                    <p><Truck className="w-3 h-3 inline mr-1" /> {courier.vehicle_type || "Non renseigné"}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700 rounded-full"
                      onClick={() => approveCourier(courier.id)}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approuver
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-red-600 hover:bg-red-50 rounded-full"
                      onClick={() => rejectCourier(courier.id)}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Rejeter
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filter */}
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Tous les utilisateurs
            </CardTitle>
            <div className="flex gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Rechercher..."
                  className="pl-9 w-64 rounded-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="h-10 px-4 rounded-full border border-input bg-background"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="all">Tous les rôles</option>
                <option value="client">Clients</option>
                <option value="partner">Partenaires</option>
                <option value="courier">Livreurs/Chauffeurs</option>
                <option value="vendor">ARTISANS</option>
              </select>
              <Button onClick={fetchUsers} variant="outline" className="rounded-full">
                🔄 Actualiser
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-12 text-muted-foreground">Chargement...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">Aucun utilisateur trouvé</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold">Utilisateur</th>
                    <th className="text-left py-3 px-4 font-semibold">Contact</th>
                    <th className="text-left py-3 px-4 font-semibold">Rôle</th>
                    <th className="text-left py-3 px-4 font-semibold">Statut</th>
                    <th className="text-left py-3 px-4 font-semibold">GPS</th>
                    <th className="text-left py-3 px-4 font-semibold">Crédits</th>
                    <th className="text-left py-3 px-4 font-semibold">Inscription</th>
                    <th className="text-right py-3 px-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className={`border-b hover:bg-slate-50 ${user.is_blocked ? 'bg-red-50' : ''}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {user.is_blocked && <Ban className="w-4 h-4 text-red-500" />}
                          <div>
                            <button type="button" onClick={() => setProfileId(user.id)} className="font-semibold hover:underline text-left" data-testid={`user-name-${user.id}`}>{user.full_name}</button>
                            {user.business_name && (
                              <p className="text-xs text-muted-foreground">{user.business_name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          <p className="flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {user.email}
                          </p>
                          <p className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="w-3 h-3" /> {user.phone || "—"}
                          </p>
                        </div>
                      </td>
                      <td className="py-3 px-4">{getRoleBadge(user.role)}</td>
                      <td className="py-3 px-4">
                        {user.is_blocked ? (
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs flex items-center gap-1 w-fit">
                            <PowerOff className="w-3 h-3" /> Désactivé
                          </span>
                        ) : user.is_approved === false ? (
                          <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs flex items-center gap-1 w-fit">
                            <Clock className="w-3 h-3" /> En attente
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1 w-fit">
                            <Power className="w-3 h-3" /> Activé
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-xs">
                          {user.latitude && user.longitude ? (
                            <a
                              href={`https://maps.google.com/?q=${user.latitude},${user.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1"
                              title={`${user.latitude?.toFixed(4)}, ${user.longitude?.toFixed(4)}`}
                            >
                              <MapPin className="w-3 h-3" />
                              {user.latitude?.toFixed(4)}, {user.longitude?.toFixed(4)}
                            </a>
                          ) : (
                            <span className="text-gray-400">Non enregistré</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium text-sm text-amber-700">
                          {(user.credit_balance || 0).toLocaleString()} FCFA
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '—'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          {user.is_blocked ? (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                              onClick={() => activateUser(user.id, user.full_name)}
                              title="Activer le compte"
                            >
                              <Power className="w-4 h-4 mr-1" />
                              Activer
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-orange-600 border-orange-300 hover:bg-orange-50 rounded-full"
                              onClick={() => deactivateUser(user.id, user.full_name)}
                              title="Désactiver le compte"
                            >
                              <PowerOff className="w-4 h-4 mr-1" />
                              Désactiver
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => deleteUser(user.id)}
                            title="Supprimer définitivement"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <UserProfileSheet userId={profileId} onClose={closeProfile} onSaved={fetchUsers} />
    </div>
  );
}
